import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "app/shadcnComponents/ui/card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getFileSyncConfig,
  getFileSyncLogs,
  selectFileSyncConfig,
  selectFileSyncLogs,
  selectGetConfigStatus,
} from "./FileSyncIntegration/slice";
import { FileSyncTab } from "./FileSyncIntegration/types";
import FileSyncConfigurationTab from "./FileSyncIntegration/FileSyncConfigurationTab";
import FileSyncHistoryTab from "./FileSyncIntegration/FileSyncHistoryTab";
import FileSyncPendingReviewTab from "./FileSyncIntegration/FileSyncPendingReviewTab";
import { cn } from "app/lib/utils";
import { IconName } from "@fortawesome/fontawesome-svg-core";

interface TTab {
  label: FileSyncTab;
  value: FileSyncTab;
  icon: IconName;
  disabled?: boolean;
}

const TAB_MAP: Record<FileSyncTab, TTab> = {
  Configuration: {
    label: "Configuration",
    value: "Configuration",
    icon: "cog",
  },
  "Sync History": {
    label: "Sync History",
    value: "Sync History",
    icon: "clock",
  },
  "Pending Review": {
    label: "Pending Review",
    value: "Pending Review",
    icon: "eye",
  },
};

const FileSyncIntegrationAdminConsoleSettings = () => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<FileSyncTab>("Configuration");

  const getConfigStatus = useAppSelector(selectGetConfigStatus);
  const config = useAppSelector(selectFileSyncConfig);
  const allLogs = useAppSelector(selectFileSyncLogs);

  const quarantinedCount = useMemo(
    () => allLogs.filter((log) => log.status === "quarantined").length,
    [allLogs]
  );

  useEffect(() => {
    if (getConfigStatus === "idle") {
      dispatch(getFileSyncConfig());
      dispatch(getFileSyncLogs());
    }
  }, [dispatch, getConfigStatus]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "Configuration":
        return <FileSyncConfigurationTab />;
      case "Sync History":
        return <FileSyncHistoryTab />;
      case "Pending Review":
        return <FileSyncPendingReviewTab />;
      default:
        return <FileSyncConfigurationTab />;
    }
  };

  const TABS: TTab[] = useMemo(() => {
    return [
      TAB_MAP["Configuration"],
      { ...TAB_MAP["Sync History"], disabled: !config },
      { ...TAB_MAP["Pending Review"], disabled: !config },
    ];
  }, [config]);

  if (getConfigStatus === "loading") {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex justify-center items-center">
            <FontAwesomeIcon
              icon={["far", "spinner"]}
              spin
              className="text-[var(--sapphire-100)] text-3xl"
            />
          </div>
        </CardContent>
      </Card>
    );
  }

  const getCardTitle = () => {
    if (!config) {
      return "File Sync";
    }
    return config.source_type === "s3" ? "S3 Integration" : "SFTP Integration";
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{getCardTitle()}</CardTitle>
          <CardDescription>
            Automatically sync employee data from your HRIS via secure file
            transfer
          </CardDescription>
        </CardHeader>
        <CardContent>
          {config ? (
            <>
              {/* Tab Navigation */}
              <div className="flex border-b border-[var(--charcoal-10)] mb-6">
                {TABS.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => {
                      if (tab.disabled) return;
                      setActiveTab(tab.value);
                    }}
                    className={cn(
                      "px-4 py-3 text-sm font-medium border-b-2 transition-colors relative flex items-center gap-2",
                      activeTab === tab.value
                        ? "border-[var(--sapphire-100)] text-[var(--sapphire-100)]"
                        : "border-transparent text-[var(--charcoal-60)] hover:text-[var(--charcoal-80)] hover:border-[var(--charcoal-20)]",
                      tab.disabled
                        ? "opacity-50 !cursor-not-allowed hover:text-[var(--charcoal-60)] hover:border-transparent"
                        : ""
                    )}
                  >
                    <FontAwesomeIcon icon={["far", tab.icon]} />
                    {tab.label}
                    {tab.value === "Pending Review" && quarantinedCount > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-[var(--error-red-10)] text-[var(--error-red-100)]">
                        {quarantinedCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {renderTabContent()}
            </>
          ) : (
            <FileSyncConfigurationTab />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FileSyncIntegrationAdminConsoleSettings;
